import axios, {
  AxiosError,
  AxiosInstance,
  InternalAxiosRequestConfig,
} from "axios";
import { AppStorage } from "../helpers/app-storage";
import { getSsoClient } from "../helpers/get-sso-client";

export const Api: AxiosInstance = axios.create({
  timeout: 60000,
  withCredentials: true,
});

interface RetriableConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

interface ApiErrorBody {
  code?: string;
  message?: string;
}

let refresher: Promise<void> | null = null;

const isRefreshRequest = (config?: InternalAxiosRequestConfig): boolean =>
  !!config?.url && /\/auth\/refresh/i.test(config.url);

const getErrorBody = (data: unknown): ApiErrorBody => {
  if (data && typeof data === "object") {
    const maybe = data as Record<string, unknown>;
    return {
      code: typeof maybe.code === "string" ? maybe.code : undefined,
      message: typeof maybe.message === "string" ? maybe.message : undefined,
    };
  }
  return {};
};

Api.interceptors.request.use((config) => {
  const currentOrgId = AppStorage.currentOrgId;
  const currentPath = AppStorage.getCurrentPath();
  const previousPath = AppStorage.previousPath;

  config.headers.set("x-request-utc", new Date().toISOString());
  config.headers.set("x-device-id", AppStorage.deviceId);
  config.headers.set("x-session-id", AppStorage.sessionId);

  if (currentOrgId) {
    config.headers.set("x-current-org-id", currentOrgId);
  }

  if (currentPath !== previousPath) {
    config.headers.set("x-client-path", currentPath);
  }

  AppStorage.previousPath = currentPath;

  return config;
});

Api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<ApiErrorBody>) => {
    const ssoClient = getSsoClient();

    // network error / timeout / CORS: nothing to do here
    if (!error.response) return Promise.reject(error);

    const config = error.config as RetriableConfig | undefined;
    const status = error.response.status;
    const { code } = getErrorBody(error.response.data);

    if (config && isRefreshRequest(config)) {
      AppStorage.clear();
      await ssoClient.redirects.signIn(window.location.href);
      return Promise.reject(error);
    }

    // 498 => invalid JWT
    if (status === 498) {
      try {
        await ssoClient.actions.signOut(window.location.origin);
      } catch {
        /* ignore */
      } finally {
        AppStorage.clear();
      }
      await ssoClient.redirects.signIn(window.location.href);
      return Promise.reject(error);
    }

    // 401 + specific code => expired token: single-flight refresh then retry once
    const isExpired = status === 401 && code === "JwtVerify.ExpiredToken";

    if (isExpired && config && !config._retry) {
      config._retry = true;

      if (!refresher) {
        refresher = (async () => {
          try {
            await ssoClient.requests.refreshToken();
          } finally {
            refresher = null;
          }
        })().catch(async () => {
          AppStorage.clear();
          await ssoClient.redirects.signIn(window.location.href);
          throw error;
        });
      }

      await refresher;
      return Api(config);
    }

    // other 401s: treat as unauthenticated
    if (status === 401) {
      AppStorage.clear();
      await ssoClient.redirects.signIn(window.location.href);
    }

    return Promise.reject(error);
  },
);
